import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import {
  Button,
  Form,
  type FormListFieldData,
  Input,
  type TableColumnsType
} from "antd"
import React from "react"

import { validateNumber } from "../../../utils/validation/number.ts"
import InputNumber from "../../Input/InputNumber.tsx"
import type {
  DocumentFormData,
  DocumentScopeSectionPhase
} from "../document.types.ts"

type FieldType = DocumentFormData["project_phases"]

interface GetColumnsArg {
  columnKey: Extract<keyof DocumentFormData, "project_phases">
  onRemove: (name: number) => void
}

export const getDocumentScopeSectionColumnsColumns = ({
  columnKey,
  onRemove
}: GetColumnsArg): TableColumnsType<FormListFieldData> => [
  {
    title: "Scope Of Work",
    dataIndex: "name",
    key: "scope",
    render: (_, field) => (
      <div className="space-y-2">
        <Form.Item<FieldType>
          name={[field.name, "name"]}
          style={{ margin: 0 }}
          rules={[{ required: true, message: "Name is required" }]}
        >
          <Input placeholder="Enter name" />
        </Form.Item>

        <Form.List name={[field.name, "checklist_data"]}>
          {(checklistFields, { add: addItem, remove: removeItem }) => (
            <div className="space-y-1">
              {checklistFields.map((checklistField) => (
                <div
                  key={checklistField.key}
                  className="flex gap-1 items-center"
                >
                  <span>•</span>
                  <Form.Item<DocumentScopeSectionPhase["checklist_data"]>
                    name={[checklistField.name, "name"]}
                    style={{ margin: 0, flex: 1 }}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: "Name is required"
                      }
                    ]}
                  >
                    <Input placeholder="Checklist item" size="small" />
                  </Form.Item>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeItem(checklistField.name)}
                  />
                </div>
              ))}
              <Button
                type="dashed"
                size="small"
                onClick={() => addItem({ name: "" })}
                icon={<PlusOutlined />}
                className="w-full"
              >
                Item
              </Button>
            </div>
          )}
        </Form.List>
      </div>
    )
  },
  {
    title: "Duration",
    width: 185,
    dataIndex: "duration_days",
    key: "duration_days",
    render: (_, field) => (
      <Form.Item<FieldType>
        name={[field.name, "duration_days"]}
        style={{ margin: 0 }}
        rules={[
          { required: false, message: "Required" },
          { validator: validateNumber }
        ]}
      >
        <InputNumber
          min={0}
          placeholder="0"
          className="w-full"
          max={99999999} // 8 digits before decimal
          suffix={
            <Form.Item<DocumentFormData> noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const val = getFieldValue([
                  columnKey,
                  field.name,
                  "duration_days"
                ])
                return val == 1 ? "day" : "days"
              }}
            </Form.Item>
          }
        />
      </Form.Item>
    )
  },
  {
    title: "Cost",
    width: 225,
    dataIndex: "name",
    key: "budget",
    render: (_, field) => (
      <Form.Item<FieldType>
        name={[field.name, "budget", "budget"]}
        style={{ margin: 0 }}
        rules={[{ validator: validateNumber }]}
      >
        <InputNumber
          stringMode
          className={"w-40"}
          min={0}
          placeholder="0"
          max={99999999.99} // 8 digits before decimal
          precision={2}
          suffix={"$"}
        />
      </Form.Item>
    )
  },
  {
    title: "",
    width: 50,
    render: (_, field) => (
      <Button
        type="text"
        danger
        size={"large"}
        icon={<DeleteOutlined />}
        onClick={() => onRemove(field.name)}
      />
    )
  }
]
